import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    filename: String
  }

  connect() {
    this.abortController = null
    // Get the shared overlay elements
    this.overlay = document.getElementById('download-overlay')
    this.progressBar = document.getElementById('overlay-progress')
    this.messageElement = document.getElementById('overlay-message')
    this.titleElement = document.getElementById('overlay-title')
    this.infoElement = document.getElementById('overlay-info')
    this.closeButton = document.getElementById('overlay-close')
    
    // Setup close button listener
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.close.bind(this))
    }
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort()
    }
    // Close button listener is automatically removed when element is destroyed
  }

  async download(event) {
    event.preventDefault()

    // Show the overlay immediately
    this.showOverlay()

    // Start the download process
    await this.performDownload()
  }

  showOverlay() {
    if (!this.overlay) return
    
    // Update content based on download type
    const isFullPressKit = this.urlValue.includes('press_kit_zip')
    const isPhotos = this.urlValue.includes('photos_zip')
    const isLogos = this.urlValue.includes('logos_zip')
    
    // Set title
    if (isFullPressKit) {
      this.titleElement.textContent = 'Press Kit'
      this.infoElement.textContent = 'This may take a few moments depending on file sizes'
    } else if (isPhotos) {
      this.titleElement.textContent = 'Photos'
      this.infoElement.textContent = 'Gathering high-resolution photos...'
    } else if (isLogos) {
      this.titleElement.textContent = 'Logos'
      this.infoElement.textContent = 'Gathering logo files in various formats...'
    }
    
    // Add overlay with fade-in animation
    this.overlay.classList.remove("hidden")
    this.overlay.classList.add("flex")
    this.overlay.style.opacity = "0"

    // Prevent body scroll when overlay is shown
    document.body.style.overflow = "hidden"

    // Animate fade-in
    requestAnimationFrame(() => {
      this.overlay.style.transition = "opacity 0.3s ease-out"
      this.overlay.style.opacity = "1"
    })

    // Start the progress animation
    this.animateProgress()
  }

  hideOverlay() {
    if (!this.overlay) return
    
    // Animate fade-out
    this.overlay.style.transition = "opacity 0.3s ease-out"
    this.overlay.style.opacity = "0"

    // Hide after animation completes
    setTimeout(() => {
      this.overlay.classList.add("hidden")
      this.overlay.classList.remove("flex")

      // Restore body scroll
      document.body.style.overflow = ""

      // Reset progress
      this.resetProgress()
    }, 300)
  }

  animateProgress() {
    // More realistic progress simulation based on content type
    this.progress = 0
    this.progressRunning = true

    // Different messages based on filename or url
    const isFullPressKit = this.urlValue.includes('press_kit_zip')
    const isPhotos = this.urlValue.includes('photos_zip')
    const isLogos = this.urlValue.includes('logos_zip')

    let messages
    if (isFullPressKit) {
      messages = [
        "Preparing your press kit...",
        "Gathering photos and logos...",
        "Processing videos...",
        "Compressing files...",
        "Creating archive...",
        "Almost ready..."
      ]
    } else if (isPhotos) {
      messages = [
        "Preparing your photos...",
        "Processing high-resolution images...",
        "Optimizing file sizes...",
        "Creating archive...",
        "Almost ready..."
      ]
    } else if (isLogos) {
      messages = [
        "Preparing your logos...",
        "Gathering vector files...",
        "Processing different formats...",
        "Creating archive...",
        "Almost ready..."
      ]
    } else {
      messages = [
        "Preparing download...",
        "Processing files...",
        "Creating archive...",
        "Almost ready..."
      ]
    }

    const updateProgress = () => {
      if (this.progress < 90 && this.progressRunning) {
        // Slower progress simulation for longer server processing
        let increment
        if (this.progress < 20) {
          increment = Math.random() * 3 + 1 // 1-4%
        } else if (this.progress < 60) {
          increment = Math.random() * 5 + 2 // 2-7%
        } else {
          increment = Math.random() * 2 + 0.5 // 0.5-2.5%
        }

        this.progress = Math.min(this.progress + increment, 90) // Cap at 90% until download completes
        this.progressBar.style.width = `${this.progress}%`

        // Update message based on progress
        const messageIndex = Math.min(
          Math.floor((this.progress / 90) * (messages.length - 1)),
          messages.length - 1
        )
        this.messageElement.textContent = messages[messageIndex]

        // Slower timing for more realistic server processing feel
        const delay = this.progress < 30 ?
          Math.random() * 800 + 1000 : // 1000-1800ms for early progress
          Math.random() * 1000 + 800   // 800-1800ms for later progress

        setTimeout(updateProgress, delay)
      }
    }

    // Start with a small delay to make it feel more natural
    setTimeout(updateProgress, 200)
  }

  resetProgress() {
    this.progressRunning = false
    this.progress = 0
    this.progressBar.style.width = "0%"
    this.progressBar.style.transition = ""
    this.progressBar.classList.remove("bg-green-500")

    // Reset message based on content type
    const isPhotos = this.urlValue.includes('photos_zip')
    const isLogos = this.urlValue.includes('logos_zip')

    if (isPhotos) {
      this.messageElement.textContent = "Preparing your photos..."
    } else if (isLogos) {
      this.messageElement.textContent = "Preparing your logos..."
    } else {
      this.messageElement.textContent = "Preparing your press kit..."
    }
  }

  async performDownload() {
    try {
      // Create abort controller for cancellation
      this.abortController = new AbortController()

      // Fetch the file - this will wait for server to generate the ZIP
      const response = await fetch(this.urlValue, {
        signal: this.abortController.signal,
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/zip'
        }
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }

      // Get the blob - this waits for the full file to download
      const blob = await response.blob()

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('content-disposition')
      let filename = this.filenameValue

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      }

      if (!filename) {
        // Fallback filename based on content type
        const isPhotos = this.urlValue.includes('photos_zip')
        const isLogos = this.urlValue.includes('logos_zip')

        if (isPhotos) {
          filename = 'photos.zip'
        } else if (isLogos) {
          filename = 'logos.zip'
        } else {
          filename = 'press_kit.zip'
        }
      }

      // Stop the progress animation
      this.progressRunning = false

      // Complete progress to 100%
      this.completeProgress()

      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename

      // Wait a bit to show completion state
      await new Promise(resolve => setTimeout(resolve, 500))

      // Trigger download
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl)
      }, 100)

      // Hide overlay after showing completion
      setTimeout(() => {
        this.hideOverlay()
      }, 1500)

    } catch (error) {
      if (error.name === 'AbortError') {
        // Download cancelled
        this.hideOverlay()
      } else {
        // Download failed - Keep console.error for legitimate error logging
        console.error("Download failed:", error)
        this.showError()
      }
    }
  }

  completeProgress() {
    // Smoothly animate to 100%
    this.progressBar.style.transition = "width 0.3s ease-out"
    this.progressBar.style.width = "100%"

    this.messageElement.textContent = "Download complete!"

    // Add completion visual feedback
    this.progressBar.classList.add("bg-green-500")
  }

  showError() {
    this.progressRunning = false
    this.messageElement.textContent = "Download failed. Please try again."
    this.messageElement.classList.add("text-red-400")

    setTimeout(() => {
      this.hideOverlay()
      this.messageElement.classList.remove("text-red-400")
    }, 2000)
  }

  // Allow manual close
  close(event) {
    if (event) {
      event.preventDefault()
    }

    // Abort any ongoing download
    if (this.abortController) {
      this.abortController.abort()
    }

    this.progressRunning = false
    this.hideOverlay()
  }
}